import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MainContainer } from "../../styles/sharedStyles";
import Title from "../../../components/title/Title";
import { tokens } from "../../../../theme";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Paper,
  List,
  ListItem,
  ListItemText,
  Collapse,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Box,
  Button,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  EmojiFoodBeverage,
  ArrowBack,
} from "@mui/icons-material";

import apiService from "../../../../ApiService";
import { API_ENDPOINTS } from "../../../../ApiService";

const RecetteDetail = () => {
  const { recetteId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const response = await apiService.get(
          API_ENDPOINTS.RECETTE_GET_ONE_SPECIFIC_RECETTE(recetteId)
        );
        console.log("Details one recette", response);
        setRecipe(response.data);
      } catch (error) {
        console.error("Error fetching recipe details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [recetteId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!recipe) {
    return (
      <Typography variant="h6" align="center">
        ⚠️ Recette introuvable
      </Typography>
    );
  }

  return (
    <MainContainer>
      <Title title={recipe.nomRecette} summary={recipe.description} />

      <Box
        m={isMobile ? 1 : 2}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Grid container justifyContent="center">
          <Grid
            item
            xs={12}
            sm={10}
            md={8}
            lg={6}
            sx={{ padding: isMobile ? "1rem" : "2rem" }}
          >
            <Paper
              sx={{
                padding: 4,
                margin: "auto",
                maxWidth: 800,
                borderRadius: 3,
                boxShadow: 3,
              }}
            >
              {/* 📸 Recipe Image */}
              <Card sx={{ mb: 3 }}>
                <CardMedia
                  component="img"
                  height="300"
                  image={
                    recipe.picturePath
                      ? recipe.picturePath
                      : "https://via.placeholder.com/300"
                  }
                  alt={recipe.nomRecette}
                />
                <CardContent>
                  <Typography variant="body1" paragraph>
                    📖 {recipe.description}
                  </Typography>

                  {/* ⏳ Time & Difficulty */}
                  <Typography variant="body2" sx={{ fontSize: 16, mt: 2 }}>
                    ⏳ <strong>Préparation:</strong> {recipe.tempsPreparation}{" "}
                    min | 🍳 <strong>Cuisson:</strong> {recipe.tempsCuisson} min
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 16, mt: 1 }}>
                    🎯 <strong>Difficulté:</strong>{" "}
                    {["⭐", "⭐⭐", "⭐⭐⭐"][recipe.difficultee - 1] || "❓"}
                  </Typography>

                  {/* 🔥 Nutrition */}
                  <Typography
                    variant="h6"
                    sx={{
                      mt: 3,
                      textDecoration: "underline",
                      textUnderlineOffset: "5px",
                    }}
                  >
                    🍏 Informations Nutritionnelles
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      transition: "0.3s",
                      "&:hover": { color: "primary.main", cursor: "pointer" },
                    }}
                  >
                    🔥 Calories: <strong>{recipe.calories} kcal</strong> | 💪{" "}
                    Protéines: <strong>{recipe.proteines}g</strong> | 🛢 Lipides:{" "}
                    <strong>{recipe.lipides}g</strong> | 🍞 Glucides:{" "}
                    <strong>{recipe.glucides}g</strong>
                  </Typography>
                </CardContent>
              </Card>

              {/* 🛒 Ingredients Section */}
              <Typography
                variant="h6"
                sx={{
                  mt: 3,
                  textDecoration: "underline",
                  textUnderlineOffset: "5px",
                }}
              >
                🛒 Ingrédients
              </Typography>
              <List>
                {recipe.ingredients?.map((ingredient, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      pl: 0,
                      transition: "0.3s",
                      "&:hover": { backgroundColor: "grey.200" },
                    }}
                  >
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          <strong>
                            {ingredient.quantite} {ingredient.mesure}
                          </strong>{" "}
                          - {ingredient.ingredientName}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {/* 📜 Steps Section - Collapsible */}
              <Typography
                variant="h6"
                sx={{
                  mt: 3,
                  textDecoration: "underline",
                  textUnderlineOffset: "5px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setExpanded(!expanded)}
              >
                📜 Étapes de Préparation{" "}
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </Typography>
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <List>
                  {recipe.steps?.map((step, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <EmojiFoodBeverage color="primary" sx={{ mr: 1 }} />
                      <ListItemText
                        primary={
                          <Typography variant="body1">
                            <strong>Étape {index + 1}:</strong>{" "}
                            {step.instructions}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>

              {/* 🌍 Culture & Category Section */}
              {recipe.culture && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  🌍 Origine: <strong>{recipe.culture}</strong>
                </Typography>
              )}
              {recipe.categories.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  📌 Catégories: {recipe.categories.map((cat) => ` ${cat}`)}
                </Typography>
              )}
            </Paper>
            {/* Centered Return Button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ArrowBack />}
                sx={{
                  backgroundColor: colors.primary[500],
                  "&:hover": { backgroundColor: colors.primary[700] },
                }}
                onClick={() => navigate("/recettes")}
              >
                Retour aux recettes
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </MainContainer>
  );
};

export default RecetteDetail;
